// src/products/dataroom/components/PartiesTeamsDisplay.js
import React, { useState, useEffect } from 'react';
import { Building2, Users, ChevronDown, ChevronRight, Plus, Edit2 } from 'lucide-react';
import partyTeamService from '../services/partyTeamService';

const PartiesTeamsDisplay = ({ projectId, userRole, onMessage }) => {
  const [parties, setParties] = useState([]);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedParties, setExpandedParties] = useState({});
  const [error, setError] = useState(null);

  console.log('PartiesTeamsDisplay rendered with projectId:', projectId);

  const loadPartiesAndTeams = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Loading parties for projectId:', projectId);
      // Load parties
      const { data: partiesData, error: partiesError } = await partyTeamService.getParties(projectId);
      console.log('Parties data:', partiesData, 'Error:', partiesError);
      if (partiesError) throw partiesError;

      setParties(partiesData || []);

      // Load teams for each party
      const teamsData = {};
      for (const party of partiesData || []) {
        const { data: partyTeams, error: teamsError } = await partyTeamService.getTeams(party.id);
        if (teamsError) {
          console.error(`Error loading teams for party ${party.id}:`, teamsError);
          teamsData[party.id] = [];
        } else {
          teamsData[party.id] = partyTeams || [];
        }
      }
      setTeams(teamsData);

      // Expand first party by default
      if (partiesData && partiesData.length > 0) {
        setExpandedParties({ [partiesData[0].id]: true });
      }
    } catch (error) {
      console.error('Error loading parties and teams:', error);
      setError(error.message);
      onMessage && onMessage('Failed to load parties and teams', 'error');
    } finally {
      setLoading(false);
    }
  }, [projectId, onMessage]);

  useEffect(() => {
    if (projectId) {
      loadPartiesAndTeams();
    }
  }, [projectId, loadPartiesAndTeams]);


  const togglePartyExpansion = (partyId) => {
    setExpandedParties(prev => ({
      ...prev,
      [partyId]: !prev[partyId]
    }));
  };

  const canManage = userRole === 'creator' || userRole === 'support';

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading parties and teams...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center text-red-600">
          <p>Error loading parties and teams: {error}</p>
        </div>
      </div>
    );
  }

  if (parties.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Parties Created</h3>
          <p className="text-gray-500 mb-4">
            Use the setup wizard to create parties and teams for this dataroom.
          </p>
          <div className="text-xs text-gray-400 mt-2">
            Debug: projectId = {projectId}, loading = {loading ? 'true' : 'false'}, error = {error || 'none'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Building2 className="h-5 w-5 mr-2" />
              Parties & Teams ({parties.length})
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Organizational structure created through the setup wizard
            </p>
          </div>
          {canManage && (
            <button
              onClick={() => window.location.href = '/dataroom/parties'}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Manage
            </button>
          )}
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {parties.map((party) => {
          const partyTeams = teams[party.id] || [];
          const isExpanded = expandedParties[party.id];

          return (
            <div key={party.id} className="p-4">
              {/* Party Header */}
              <div 
                className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2"
                onClick={() => togglePartyExpansion(party.id)}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" style={{ color: party.color }} />
                    ) : (
                      <ChevronRight className="w-4 h-4" style={{ color: party.color }} />
                    )}
                  </div>
                  <div 
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: party.color }}
                  />
                  <span 
                    className="px-2 py-1 text-xs font-bold rounded text-white"
                    style={{ backgroundColor: party.color }}
                  >
                    {party.code}
                  </span>
                  <div>
                    <h3 className="font-medium text-gray-900">{party.name}</h3>
                    <p className="text-sm text-gray-500">
                      {partyTeams.length} team{partyTeams.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Teams Section */}
              {isExpanded && (
                <div className="mt-3 ml-8 space-y-2">
                  {partyTeams.length === 0 ? (
                    <div className="text-center py-4 border-2 border-dashed border-gray-300 rounded-lg">
                      <Users className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No teams created for this party</p>
                    </div>
                  ) : (
                    partyTeams.map((team) => (
                      <div key={team.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: party.color }}
                          />
                          <span 
                            className="px-2 py-1 text-xs font-medium rounded"
                            style={{ backgroundColor: `${party.color}20`, color: party.color }}
                          >
                            {team.code}
                          </span>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">{team.name}</h4>
                            {team.description && (
                              <p className="text-xs text-gray-500">{team.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">
                            Team permissions
                          </span>
                          {canManage && (
                            <button
                              className="p-1 text-gray-400 hover:text-blue-600 rounded"
                              title="Manage team"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PartiesTeamsDisplay;
